"use client";

import React from "react";
import { SimpleTable } from "./SimpleTable";
import { SimpleTableMobileDetailModal } from "./SimpleTableMobileDetailModal";
import { GroupingWrapper } from "../GroupingWrapper";
import { ColumnManagerProvider } from "@/lib/column-manager";
import { createColumnsFromConfig } from "@/lib/utils/columnFactory";
import type { SimpleTableConfig, BaseEntity } from "./types";

// ============================================================================
// DEMO CONFIGURATION
// ============================================================================

interface DemoTask extends BaseEntity {
  id: string;
  title: string;
  status: "pending" | "in_progress" | "completed";
  priority: "low" | "medium" | "high";
  dueDate: string;
  assignee: string;
}

const demoConfig: SimpleTableConfig<DemoTask> = {
  entity: "tasks",
  displayName: "Tasks",
  statusFieldId: null,
  fields: {
    title: {
      label: "Title",
      type: "text",
      primary: true,
    },
    status: {
      label: "Status",
      type: "select",
      options: [
        { value: "pending", label: "Pending" },
        { value: "in_progress", label: "In Progress" },
        { value: "completed", label: "Completed" },
      ],
      badge: true,
    },
    priority: {
      label: "Priority",
      type: "select",
      options: [
        { value: "low", label: "Low" },
        { value: "medium", label: "Medium" },
        { value: "high", label: "High" },
      ],
      badge: true,
    },
    dueDate: {
      label: "Due Date",
      type: "date",
    },
    assignee: {
      label: "Assignee",
      type: "text",
    },
  },
  features: {
    search: true,
    filters: true,
    export: true,
    bulkActions: true,
    savedViews: true,
  },
  actions: {
    create: true,
    edit: true,
    delete: true,
    view: true,
  },
  bulkActions: [
    {
      id: "mark_completed",
      label: "Mark Completed",
      icon: "check",
      variant: "default",
    },
    {
      id: "delete_selected",
      label: "Delete Selected",
      icon: "trash",
      variant: "destructive",
    },
  ],
};

// Demo data
const demoData: DemoTask[] = [
  {
    id: "1",
    title: "Design new landing page",
    status: "in_progress",
    priority: "high",
    dueDate: "2024-01-15",
    assignee: "John Doe",
  },
  {
    id: "2",
    title: "Update user documentation",
    status: "pending",
    priority: "medium",
    dueDate: "2024-01-20",
    assignee: "Jane Smith",
  },
  {
    id: "3",
    title: "Fix mobile responsive issues",
    status: "completed",
    priority: "high",
    dueDate: "2024-01-10",
    assignee: "Bob Johnson",
  },
  {
    id: "4",
    title: "Implement three dots menu",
    status: "in_progress",
    priority: "medium",
    dueDate: "2024-01-18",
    assignee: "Alice Brown",
  },
  {
    id: "5",
    title: "Add pagination support",
    status: "pending",
    priority: "low",
    dueDate: "2024-01-25",
    assignee: "Charlie Wilson",
  },
];

// ============================================================================
// DEMO COMPONENT
// ============================================================================

export function SimpleTableMobileDemo() {
  // Mock handlers
  const handleEdit = (task: DemoTask) => {
    console.log("Edit task:", task);
    alert(`Edit task: ${task.title}`);
  };

  const handleDelete = (task: DemoTask) => {
    console.log("Delete task:", task);
    alert(`Delete task: ${task.title}`);
  };

  const handleView = (task: DemoTask) => {
    console.log("View task:", task);
    alert(`View task: ${task.title}`);
  };

  const handleInlineEdit = async (updatedTask: DemoTask) => {
    console.log("Inline edit task:", updatedTask);
  };

  const handleCreate = () => {
    console.log("Create new task");
    alert("Create new task");
  };

  const handleBulkAction = (actionId: string, selectedTasks: DemoTask[]) => {
    console.log("Bulk action:", actionId, selectedTasks);
    alert(`Bulk action: ${actionId} on ${selectedTasks.length} tasks`);
  };

  // Mock hooks for demo - must implement full EntityHooks interface
  const mockHooks = {
    usePaginated: () => ({ data: undefined, isLoading: false, error: null, refetch: () => {} }),
    useInfinite: () => ({
      data: undefined,
      isLoading: false,
      error: null,
      refetch: () => {},
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => {},
    }),
    useSingle: () => ({ data: undefined, isLoading: false, error: null, refetch: () => {} }),
    useCreate: () => ({ mutateAsync: async () => ({}) as DemoTask, isPending: false }),
    useUpdate: () => ({ mutateAsync: async () => ({}) as DemoTask, isPending: false }),
    useDelete: () => ({ mutateAsync: async () => "", isPending: false }),
  } as any;

  // Create default columns from config
  const defaultColumns = React.useMemo(
    () => createColumnsFromConfig<DemoTask>(demoConfig),
    [demoConfig]
  );

  return (
    <ColumnManagerProvider
      storageKey="simple-table-mobile-demo-column-preferences"
      defaultColumns={defaultColumns}
    >
      <div className="min-h-screen bg-card-50 p-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold text-card-foreground mb-8">SimpleTable Mobile Demo</h1>

          <div className="bg-card rounded-lg shadow-sm border">
            <GroupingWrapper<DemoTask> config={demoConfig} data={demoData}>
              {({ processedData: groupedData, groupingContext }) => (
                <SimpleTable<DemoTask>
                  config={demoConfig}
                  // SimpleTable's `data` is typed as `TData[]` but the runtime
                  // list mixes data rows and synthesised group-header rows
                  // produced by `GroupingWrapper`. The table inspects each
                  // row's `type` discriminator at render time. See
                  // TableBodyRows.tsx.
                  data={groupedData as DemoTask[]}
                  isLoading={false}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onInlineEdit={handleInlineEdit}
                  onCreate={handleCreate}
                  onBulkAction={handleBulkAction}
                  hooks={mockHooks}
                  groupingContext={groupingContext}
                />
              )}
            </GroupingWrapper>
          </div>

          <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-950/40 rounded-lg">
            <h2 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
              ✅ Three Dots Menu Features
            </h2>
            <ul className="text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Tap the three dots (⋮) on any card to open the action menu</li>
              <li>• View Details - Opens the item in a detail modal</li>
              <li>• Edit - Triggers the edit action</li>
              <li>• Delete - Triggers the delete action (with red styling)</li>
              <li>• Custom actions via ActionRenderer integration</li>
              <li>• Menu prevents card click when opened</li>
            </ul>
          </div>

          <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-950/40 rounded-lg">
            <h2 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2">📱 Mobile Optimizations</h2>
            <ul className="text-yellow-800 dark:text-yellow-200 space-y-1">
              <li>• Touch-optimized button sizes and spacing</li>
              <li>• Proper event handling to prevent conflicts</li>
              <li>• Mobile-friendly dropdown positioning</li>
              <li>• Responsive design for all screen sizes</li>
              <li>• Loading states and error handling</li>
            </ul>
          </div>
        </div>
      </div>
    </ColumnManagerProvider>
  );
}
